import React, { useState, useEffect } from 'react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';
import { FaUsers, FaUserClock, FaUserPlus, FaCalendarCheck, FaUserGraduate, FaHandshake, FaFileInvoiceDollar, FaAward, FaCalendarAlt, FaFilter, FaUserCheck, FaBirthdayCake, FaWhatsapp, FaMagic, FaDownload, FaPaperPlane, FaTimes, FaCheck, FaSyncAlt } from 'react-icons/fa';
import { MdRefresh } from 'react-icons/md';
import axios from 'axios';
import html2canvas from 'html2canvas';
import BirthdayCard, { generateBirthdayPosterCanvas } from '../PosterDesign/BirthDay/BirthdayCard';

const COLORS = ['#10b981', '#f59e0b', '#ef4444', '#6b7280']; 

interface DashbordOverviewProps {
  onNavigateToMembers?: (filter: string) => void;
  onNavigateTab?: (tab: string) => void;
}

const DashbordOverview: React.FC<DashbordOverviewProps> = ({ onNavigateToMembers, onNavigateTab }) => {
  const [stats, setStats] = useState<{
    total_members: number;
    active_members?: number;
    pending_approval: number;
    inactive_members: number;
    deleted_members: number;
    new_members: number;
    renewals_pending: number;
    grace_period: number;
    expired: number;
  }>({
    total_members: 0,
    active_members: 0,
    pending_approval: 0,
    inactive_members: 0,
    deleted_members: 0,
    new_members: 0,
    renewals_pending: 0,
    grace_period: 0,
    expired: 0
  });

  const [societyYear, setSocietyYear] = useState('2026-2027');
  const [selectedMonth, setSelectedMonth] = useState('ALL');

  const [activityMetrics, setActivityMetrics] = useState({
    r2r_count: 0,
    business_generated_amount: 0,
    pending_verification_amount: 0,
    visitor_count: 0,
    referral_count: 0,
    total_activities_submitted: 0,
    total_verified_count: 0,
    verified_ratio: '0 / 0'
  });

  const [flaggedMembers, setFlaggedMembers] = useState<any[]>([]);
  const [upcomingBirthdays, setUpcomingBirthdays] = useState<any[]>([]);

  // Birthday Wish & Flyer Modal States
  const [selectedBirthdayMember, setSelectedBirthdayMember] = useState<any | null>(null);
  const [birthdayModalMode, setBirthdayModalMode] = useState<'WISH' | 'FLYER' | null>(null);
  const [isSendingWa, setIsSendingWa] = useState(false);
  const [waSendResult, setWaSendResult] = useState<{ success: boolean; message: string } | null>(null);
  const flyerPosterRef = React.useRef<HTMLDivElement>(null);
  const [flyerTitle, setFlyerTitle] = useState<string>('Happy Birthday');
  const [flyerMessage, setFlyerMessage] = useState<string>(
    "RMB Jaipur Family wishes a Happy Birthday to our\nincredible Member!\nMay this year bring you success, happiness, and\nfulfillment"
  );

  const formatBirthdayDateForPoster = (dob: any, formattedDateStr: string = '') => {
    if (formattedDateStr && /^[0-9]+\s+[A-Za-z]+$/.test(formattedDateStr.trim())) {
      return formattedDateStr.trim();
    }
    if (!dob) return formattedDateStr || 'Special Day';
    try {
      const raw = String(dob).split('T')[0];
      const d = new Date(raw);
      if (!isNaN(d.getTime())) {
        const day = d.getDate();
        const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
        return `${day} ${months[d.getMonth()]}`;
      }
    } catch {}
    return formattedDateStr || 'Special Day';
  };

  const [monthlyChartData] = useState<any[]>([
    { name: 'Jul', newMembers: 2, renewals: 4 },
    { name: 'Aug', newMembers: 3, renewals: 2 },
    { name: 'Sep', newMembers: 4, renewals: 5 },
    { name: 'Oct', newMembers: 1, renewals: 3 },
    { name: 'Nov', newMembers: 5, renewals: 6 },
    { name: 'Dec', newMembers: 2, renewals: 4 },
    { name: 'Jan', newMembers: 4, renewals: 3 },
    { name: 'Feb', newMembers: 3, renewals: 2 },
    { name: 'Mar', newMembers: 6, renewals: 5 },
    { name: 'Apr', newMembers: 2, renewals: 4 },
    { name: 'May', newMembers: 4, renewals: 3 },
    { name: 'Jun', newMembers: 5, renewals: 7 },
  ]);

  const fetchFlaggedMembers = async () => {
    try {
      const res = await axios.get('https://rmbbackend.rmbjaipur.com/auth/attendance/flagged-absent-members');
      setFlaggedMembers(res.data.flaggedMembers || []);
    } catch {
      // Silent — absence API may not have data yet
    }
  };

  const fetchUpcomingBirthdays = async () => {
    try {
      const res = await axios.get('https://rmbbackend.rmbjaipur.com/auth/upcoming-birthdays');
      const list = res.data.upcomingBirthdays || [];
      setUpcomingBirthdays(list);

      // If anyone has a birthday today (daysUntil === 0), verify automated wish trigger
      const todaysMembers = list.filter((m: any) => m.daysUntil === 0);
      if (todaysMembers.length > 0) {
        axios.get('https://rmbbackend.rmbjaipur.com/auth/check-todays-birthdays')
          .then((checkRes) => {
            console.log('Automated birthday checker verified:', checkRes.data);
          })
          .catch(() => {});
      }
    } catch {
      // Silent
    }
  };

  // Dispatch GlobeBot WhatsApp Birthday Template
  const handleSendBirthdayTemplate = async (member: any, forceMediaUrl?: string) => {
    setIsSendingWa(true);
    setWaSendResult(null);

    let mediaUrlToUse = forceMediaUrl || member.birthday_poster || '';
    let base64ToUpload: string | null = null;

    if (flyerPosterRef.current) {
      try {
        let canvas: HTMLCanvasElement | null = null;
        if ((flyerPosterRef.current as any).getPosterCanvas) {
          canvas = await (flyerPosterRef.current as any).getPosterCanvas();
        } else {
          canvas = await generateBirthdayPosterCanvas({
            image: member.profile
              ? (member.profile.startsWith('http')
                  ? member.profile
                  : `https://rmbbackend.rmbjaipur.com/${member.profile.replace(/\\/g, '/').replace(/^\/+/, '')}`)
              : undefined,
            name: member.name,
            date: formatBirthdayDateForPoster(member.dob, member.formattedDate),
            title: flyerTitle,
            message: flyerMessage
          });
        }
        if (canvas) {
          base64ToUpload = canvas.toDataURL('image/png');
        }
      } catch (canvasErr) {
        console.warn('Canvas generator notice, falling back to html2canvas:', canvasErr);
        try {
          const fallbackCanvas = await html2canvas(flyerPosterRef.current, {
            useCORS: true,
            scale: 2,
            backgroundColor: '#243758',
            logging: false
          });
          base64ToUpload = fallbackCanvas.toDataURL('image/png');
        } catch {}
      }
    }

    if (!mediaUrlToUse && !base64ToUpload) {
      if (member.profile) {
        mediaUrlToUse = member.profile.startsWith('http')
          ? member.profile
          : `https://rmbbackend.rmbjaipur.com/${member.profile.replace(/\\/g, '/').replace(/^\/+/, '')}`;
      } else {
        mediaUrlToUse = 'https://rmbjaipur.com/static/media/logo.f7e53f10137d7a7ffeb3.png';
      }
    }

    try {
      // 1. Send via Backend Endpoint (which uploads base64 flyer and triggers GlobeBot)
      const res = await axios.post('https://rmbbackend.rmbjaipur.com/auth/send-birthday-wishes', {
        memberId: member.id,
        memberName: member.name,
        memberNumber: member.number,
        mediaUrl: mediaUrlToUse,
        base64Image: base64ToUpload,
        variables: [member.name, 'RMB Jaipur']
      });

      if (res.data?.success) {
        const savedUrl = res.data.mediaUrl || mediaUrlToUse;
        setUpcomingBirthdays(prev => prev.map(m => m.id === member.id ? { ...m, birthday_poster: savedUrl } : m));
        setWaSendResult({
          success: true,
          message: `🎉 WhatsApp birthday template sent successfully to ${member.name} (${member.number || 'Member'}) and Admin (917301020313)!`
        });
        setIsSendingWa(false);
        return;
      }
    } catch {
      // 2. Direct Fallback to GlobeBot API
      try {
        const formatPhone = (raw: string) => {
          if (!raw) return '';
          const digits = String(raw).replace(/\D/g, '');
          if (digits.length === 10) return `91${digits}`;
          if (digits.length === 11 && digits.startsWith('0')) return `91${digits.slice(1)}`;
          if (digits.startsWith('91')) return digits;
          return digits;
        };

        const cleanNum = formatPhone(member.number);
        const targetNumbers = Array.from(new Set([cleanNum, '917301020313'].filter(Boolean)));
        const fallbackUrl = mediaUrlToUse || (member.profile ? (member.profile.startsWith('http') ? member.profile : `https://rmbbackend.rmbjaipur.com/${member.profile}`) : 'https://rmbjaipur.com/static/media/logo.f7e53f10137d7a7ffeb3.png');

        const gbRes = await fetch('https://wa-api.globebot.io/api/v22/campaigns/create', {
          method: 'POST',
          headers: {
            'x-api-key': 'gwa_de920caf7974ef1d1bf6b723cd06e1f9',
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            phone_number_id: '923958910800315',
            numbers: targetNumbers,
            template_name: 'birthday_wishes',
            template_language: 'en_US',
            variablesMap: [member.name, 'RMB Jaipur'],
            headerComponent: {
              format: 'IMAGE',
              mediaUrl: fallbackUrl
            }
          })
        });

        const gbData = await gbRes.json().catch(() => ({}));
        if (gbRes.ok && gbData.success) {
          setWaSendResult({
            success: true,
            message: `🎉 WhatsApp birthday template sent successfully to ${member.name} (${member.number || 'Member'}) and Admin (917301020313)!`
          });
        } else {
          setWaSendResult({
            success: false,
            message: `GlobeBot notice: ${gbData.message || 'Template request processed'}`
          });
        }
      } catch (err: any) {
        setWaSendResult({
          success: false,
          message: `Error sending template: ${err.message || 'Network error'}`
        });
      }
    } finally {
      setIsSendingWa(false);
    }
  };

  // Download Flyer PNG
  const handleDownloadFlyer = async (memberName: string) => {
    try {
      const memberPhoto = selectedBirthdayMember?.profile
        ? (selectedBirthdayMember.profile.startsWith('http')
            ? selectedBirthdayMember.profile
            : `https://rmbbackend.rmbjaipur.com/${selectedBirthdayMember.profile.replace(/\\/g, '/').replace(/^\/+/, '')}`)
        : undefined;

      let dataUrl = '';
      if (flyerPosterRef.current && (flyerPosterRef.current as any).getPosterCanvas) {
        const canvas = await (flyerPosterRef.current as any).getPosterCanvas();
        dataUrl = canvas.toDataURL('image/png');
      } else {
        const canvas = await generateBirthdayPosterCanvas({
          image: memberPhoto,
          name: selectedBirthdayMember?.name || memberName,
          date: formatBirthdayDateForPoster(selectedBirthdayMember?.dob, selectedBirthdayMember?.formattedDate),
          title: flyerTitle,
          message: flyerMessage
        });
        dataUrl = canvas.toDataURL('image/png');
      }

      const link = document.createElement('a');
      link.download = `Birthday_Flyer_${memberName.replace(/\s+/g, '_')}.png`;
      link.href = dataUrl;
      link.click();
    } catch (e) {
      console.warn('Canvas generator notice, falling back to html2canvas:', e);
      if (flyerPosterRef.current) {
        try {
          const canvas = await html2canvas(flyerPosterRef.current, {
            useCORS: true,
            scale: 2,
            backgroundColor: '#243758',
            logging: false
          });
          const dataUrl = canvas.toDataURL('image/png');
          const link = document.createElement('a');
          link.download = `Birthday_Flyer_${memberName.replace(/\s+/g, '_')}.png`;
          link.href = dataUrl;
          link.click();
        } catch (fallbackErr) {
          console.error('Failed to download flyer:', fallbackErr);
        }
      }
    }
  };

  const fetchStats = async () => {
    try {
      const res = await axios.get('https://rmbbackend.rmbjaipur.com/auth/dashboard-stats');
      setStats(res.data);
    } catch {
      // Silent
    }
  };

  const fetchActivityMetrics = async () => {
    try {
      const res = await axios.get('https://rmbbackend.rmbjaipur.com/auth/dashboard-activity-metrics', {
        params: { society_year: societyYear, month: selectedMonth }
      });
      setActivityMetrics(res.data);
    } catch {
      // Silent
    }
  };

  useEffect(() => {
    fetchStats();
    fetchActivityMetrics();
    fetchFlaggedMembers();
    fetchUpcomingBirthdays();

    const interval = setInterval(() => {
      fetchStats();
      fetchActivityMetrics();
      fetchFlaggedMembers();
      fetchUpcomingBirthdays();
    }, 30000);

    return () => clearInterval(interval);
  }, [societyYear, selectedMonth]);

  const activeMembersCount = (stats as any).active_members !== undefined ? (stats as any).active_members : Math.max(0, stats.total_members - (stats.inactive_members || 0));

  const pieData = [
    { name: 'Active Members', value: activeMembersCount },
    { name: 'Grace Period (45d)', value: stats.grace_period },
    { name: 'Inactive Members', value: stats.inactive_members },
    { name: 'Deleted (Trash)', value: stats.deleted_members },
  ];

  return (
    <div className="p-6 bg-[#f4f7f6] min-h-screen space-y-6">
      {/* Top Header */}
      <div className="bg-white dark:bg-[#1E2B45] p-4 sm:p-6 rounded-2xl shadow-sm border border-slate-200/80 dark:border-[#2A3B5C] flex flex-col md:flex-row justify-between items-start md:items-center gap-3 sm:gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-black text-[#0A2451] dark:text-white">RMB Jaipur Executive Dashboard</h1>
          <p className="text-[11px] sm:text-xs text-gray-500 dark:text-slate-400 mt-1">Real-time society year metrics, business generated, and membership distribution</p>
        </div>

        <div className="flex items-center gap-2 sm:gap-3 w-full md:w-auto">
          <button 
            onClick={() => { fetchStats(); fetchActivityMetrics(); fetchFlaggedMembers(); }}
            className="flex-1 md:flex-none justify-center bg-[#0A2451] text-amber-400 px-4 py-2.5 rounded-xl shadow-md hover:bg-blue-900 transition flex items-center gap-1.5 text-xs font-black cursor-pointer border border-blue-400/20"
          >
            <MdRefresh size={18} /> Refresh Stats
          </button>
        </div>
      </div>

      {/* ⚠️ 3+ CONTINUOUS ABSENCES WARNING CARD FOR LEADERSHIP */}
      {flaggedMembers.length > 0 && (
        <div className="bg-gradient-to-r from-red-600 via-rose-600 to-amber-600 p-4 sm:p-5 rounded-3xl shadow-xl text-white space-y-3">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center border-b border-white/20 pb-2 gap-2">
            <h3 className="text-xs sm:text-sm font-black flex items-center gap-2 uppercase tracking-wide">
              ⚠️ ATTENTION LEADERSHIP: {flaggedMembers.length} MEMBER(S) WITH 3+ CONTINUOUS ABSENCES
            </h3>
            <span className="text-[10px] bg-white text-red-700 font-extrabold px-3 py-1 rounded-full uppercase shadow-xs">Action Required</span>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
            {flaggedMembers.map((m: any) => (
              <div key={m.id} className="bg-white/15 backdrop-blur-md p-3 rounded-2xl border border-white/20 flex items-center gap-3">
                <img
                  src={m.profile ? (m.profile.startsWith('http') ? m.profile : `https://rmbbackend.rmbjaipur.com/${m.profile}`) : 'https://via.placeholder.com/150'}
                  alt={m.name}
                  className="w-10 h-10 rounded-full object-cover border-2 border-white/50"
                />
                <div>
                  <h4 className="font-extrabold text-white text-xs leading-snug">{m.name}</h4>
                  <p className="text-[10px] text-red-100 font-medium">{m.company_name || 'Member'}</p>
                  <div className="text-[10px] font-black text-amber-300 mt-0.5">
                    🚨 {m.consecutive_absences} Consecutive Absences
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Society Year & Month Filter Controls Bar */}
      <div className="bg-white dark:bg-[#1E2B45] p-3.5 sm:p-4 rounded-2xl shadow-sm border border-slate-200/80 dark:border-[#2A3B5C] flex flex-col md:flex-row justify-between items-start md:items-center gap-3">
        <div className="flex items-center gap-2">
          <FaFilter className="text-[#0A2451] dark:text-amber-400" />
          <span className="text-xs font-bold text-gray-700 dark:text-slate-300 uppercase tracking-wider">Society Year & Month Filters:</span>
        </div>

        <div className="flex flex-wrap items-center gap-2.5 w-full md:w-auto">
          <div className="flex items-center gap-1.5 bg-gray-50 dark:bg-slate-800 p-1.5 rounded-xl border border-slate-200/80 dark:border-slate-700 flex-1 sm:flex-none">
            <span className="text-[11px] font-bold text-gray-500 dark:text-slate-400 pl-1">Society Year:</span>
            <select
              value={societyYear}
              onChange={(e) => setSocietyYear(e.target.value)}
              className="py-1 px-2.5 outline-none text-xs font-bold text-[#0A2451] dark:text-amber-300 bg-white dark:bg-slate-900 rounded-lg border border-gray-300 dark:border-slate-700"
            >
              <option value="2023-2024">2023-2024</option>
              <option value="2024-2025">2024-2025</option>
              <option value="2025-2026">2025-2026</option>
              <option value="2026-2027">2026-2027</option>
            </select>
          </div>

          <div className="flex items-center gap-1.5 bg-gray-50 dark:bg-slate-800 p-1.5 rounded-xl border border-slate-200/80 dark:border-slate-700 flex-1 sm:flex-none">
            <span className="text-[11px] font-bold text-gray-500 dark:text-slate-400 pl-1">Month:</span>
            <select
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
              className="py-1 px-2.5 outline-none text-xs font-bold text-gray-800 dark:text-slate-200 bg-white dark:bg-slate-900 rounded-lg border border-gray-300 dark:border-slate-700"
            >
              <option value="ALL">Complete Society Year (All Months)</option>
              <option value="7">July</option>
              <option value="8">August</option>
              <option value="9">September</option>
              <option value="10">October</option>
              <option value="11">November</option>
              <option value="12">December</option>
              <option value="1">January</option>
              <option value="2">February</option>
              <option value="3">March</option>
              <option value="4">April</option>
              <option value="5">May</option>
              <option value="6">June</option>
            </select>
          </div>
        </div>
      </div>

      {/* 💼 REFERRAL & ALL ACTIVITIES VERIFICATION DASHBOARD SUMMARY (EXACT PROMPT DESIGN) */}
      <div 
        onClick={() => onNavigateTab && onNavigateTab('ActivityVerification')}
        className="bg-[#131E32] p-5 rounded-3xl border border-[#2A3B5C] shadow-2xl space-y-4 cursor-pointer hover:border-amber-400/50 transition text-slate-100"
      >
        <div className="flex justify-between items-center border-b border-[#2A3B5C] pb-2">
          <div className="flex items-center gap-2">
            <span className="text-amber-400 text-lg">💼</span>
            <h3 className="font-black text-white text-sm uppercase tracking-wider">
              Referral &amp; All Activities Verification Dashboard Summary
            </h3>
          </div>
          <span className="text-xs text-amber-400 font-bold hover:underline flex items-center gap-1">
            Go to Verification Audit &rarr;
          </span>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-[#1E2B45] p-4 rounded-2xl border border-[#2A3B5C] flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-blue-500/20 text-blue-400 font-black flex items-center justify-center border border-blue-500/30 text-lg">
              💼
            </div>
            <div>
              <div className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">TOTAL ACTIVITIES SUBMITTED</div>
              <div className="text-2xl font-black text-white mt-0.5">{activityMetrics.total_activities_submitted || 0}</div>
            </div>
          </div>

          <div className="bg-[#1E2B45] p-4 rounded-2xl border border-emerald-500/30 flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-emerald-500/20 text-emerald-400 font-black flex items-center justify-center border border-emerald-500/30 text-lg">
              💵
            </div>
            <div>
              <div className="text-[10px] text-emerald-400 font-bold uppercase tracking-wider">VERIFIED CLOSED BUSINESS</div>
              <div className="text-xl sm:text-2xl font-black text-emerald-400 mt-0.5">₹{Number(activityMetrics.business_generated_amount || 0).toLocaleString()}</div>
            </div>
          </div>

          <div className="bg-[#1E2B45] p-4 rounded-2xl border border-amber-500/30 flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-amber-500/20 text-amber-400 font-black flex items-center justify-center border border-amber-500/30 text-lg">
              ⏳
            </div>
            <div>
              <div className="text-[10px] text-amber-400 font-bold uppercase tracking-wider">PENDING VERIFICATION</div>
              <div className="text-xl sm:text-2xl font-black text-amber-400 mt-0.5">₹{Number(activityMetrics.pending_verification_amount || 0).toLocaleString()}</div>
            </div>
          </div>

          <div className="bg-[#1E2B45] p-4 rounded-2xl border border-purple-500/30 flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-purple-500/20 text-purple-400 font-black flex items-center justify-center border border-purple-500/30 text-lg">
              ✓
            </div>
            <div>
              <div className="text-[10px] text-purple-300 font-bold uppercase tracking-wider">VERIFIED RATIO</div>
              <div className="text-2xl font-black text-purple-300 mt-0.5">{activityMetrics.verified_ratio || '0 / 0'}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Row 1: Filtered Activity & Business Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
        {/* R2R Meetings Card */}
        <div 
          onClick={() => onNavigateTab && onNavigateTab('ReportsAnalytics')}
          className="bg-gradient-to-br from-purple-800 to-indigo-700 p-5 rounded-2xl shadow-lg text-white relative overflow-hidden flex flex-col justify-between h-32 cursor-pointer hover:scale-[1.02] transition-all"
        >
          <div className="flex justify-between items-start">
            <div className="bg-white/20 p-2 rounded-lg">
              <FaUserCheck size={22} />
            </div>
            <div className="text-right">
              <h2 className="text-3xl font-black">{activityMetrics.r2r_count}</h2>
              <p className="text-xs font-bold opacity-90">R2R One-on-Ones</p>
            </div>
          </div>
          <p className="text-[11px] text-right mt-1 opacity-80">Logged in {societyYear} (Click for Details)</p>
          <div className="absolute bottom-0 left-0 h-1 bg-white/40 w-full" />
        </div>

        {/* Business Generated Card */}
        <div 
          onClick={() => onNavigateTab && onNavigateTab('ReportsAnalytics')}
          className="bg-gradient-to-r from-emerald-600 to-teal-500 p-5 rounded-xl shadow-md text-white relative overflow-hidden flex flex-col justify-between h-32 cursor-pointer hover:scale-[1.02] transition-transform"
        >
          <div className="flex justify-between items-start">
            <div className="bg-white/20 p-2 rounded-lg">
              <FaFileInvoiceDollar size={22} />
            </div>
            <div className="text-right">
              <h2 className="text-2xl font-black">₹{Number(activityMetrics.business_generated_amount).toLocaleString()}</h2>
              <p className="text-xs font-bold opacity-90">Business Closed (TYFCB)</p>
            </div>
          </div>
          <p className="text-[11px] text-right mt-1 opacity-80">Total value generated (Click for Details)</p>
          <div className="absolute bottom-0 left-0 h-1 bg-white/40 w-full" />
        </div>

        {/* Visitors Invited Card */}
        <div 
          onClick={() => onNavigateTab && onNavigateTab('ActivityVerification')}
          className="bg-gradient-to-r from-amber-500 to-orange-500 p-5 rounded-xl shadow-md text-white relative overflow-hidden flex flex-col justify-between h-32 cursor-pointer hover:scale-[1.02] transition-transform"
        >
          <div className="flex justify-between items-start">
            <div className="bg-white/20 p-2 rounded-lg">
              <FaUserPlus size={22} />
            </div>
            <div className="text-right">
              <h2 className="text-3xl font-black">{activityMetrics.visitor_count}</h2>
              <p className="text-xs font-bold opacity-90">Visitors / Guests</p>
            </div>
          </div>
          <p className="text-[11px] text-right mt-1 opacity-80">Invited by members (Click for Details)</p>
          <div className="absolute bottom-0 left-0 h-1 bg-white/40 w-full" />
        </div>

        {/* Referrals Passed Card */}
        <div 
          onClick={() => onNavigateTab && onNavigateTab('ActivityVerification')}
          className="bg-gradient-to-r from-blue-600 to-cyan-600 p-5 rounded-xl shadow-md text-white relative overflow-hidden flex flex-col justify-between h-32 cursor-pointer hover:scale-[1.02] transition-transform"
        >
          <div className="flex justify-between items-start">
            <div className="bg-white/20 p-2 rounded-lg">
              <FaHandshake size={22} />
            </div>
            <div className="text-right">
              <h2 className="text-3xl font-black">{activityMetrics.referral_count}</h2>
              <p className="text-xs font-bold opacity-90">Referrals Passed</p>
            </div>
          </div>
          <p className="text-[11px] text-right mt-1 opacity-80">Passed in chapter (Click for Details)</p>
          <div className="absolute bottom-0 left-0 h-1 bg-white/40 w-full" />
        </div>
      </div>

      {/* Row 2: Roster Status Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
        <div 
          onClick={() => onNavigateToMembers && onNavigateToMembers('active')}
          className="bg-white p-4 rounded-xl shadow-sm border border-gray-200 cursor-pointer hover:border-emerald-400 transition"
        >
          <div className="flex justify-between items-center">
            <div>
              <p className="text-[11px] font-bold text-gray-500 uppercase">Active Strength</p>
              <h3 className="text-2xl font-black text-emerald-600 mt-0.5">{activeMembersCount}</h3>
            </div>
            <div className="bg-emerald-50 text-emerald-600 p-3 rounded-xl border border-emerald-200">
              <FaUsers size={20} />
            </div>
          </div>
        </div>

        <div 
          onClick={() => onNavigateToMembers && onNavigateToMembers('grace')}
          className="bg-white p-4 rounded-xl shadow-sm border border-gray-200 cursor-pointer hover:border-amber-400 transition"
        >
          <div className="flex justify-between items-center">
            <div>
              <p className="text-[11px] font-bold text-gray-500 uppercase">Grace Period (45d)</p>
              <h3 className="text-2xl font-black text-amber-600 mt-0.5">{stats.grace_period}</h3>
            </div>
            <div className="bg-amber-50 text-amber-600 p-3 rounded-xl border border-amber-200">
              <FaUserClock size={20} />
            </div>
          </div>
        </div>

        <div 
          onClick={() => onNavigateToMembers && onNavigateToMembers('inactive')}
          className="bg-white p-4 rounded-xl shadow-sm border border-gray-200 cursor-pointer hover:border-rose-400 transition"
        >
          <div className="flex justify-between items-center">
            <div>
              <p className="text-[11px] font-bold text-gray-500 uppercase">Inactive Members</p>
              <h3 className="text-2xl font-black text-rose-600 mt-0.5">{stats.inactive_members}</h3>
            </div>
            <div className="bg-rose-50 text-rose-600 p-3 rounded-xl border border-rose-200">
              <FaUsers size={20} />
            </div>
          </div>
        </div>

        <div 
          onClick={() => onNavigateToMembers && onNavigateToMembers('deleted')}
          className="bg-white p-4 rounded-xl shadow-sm border border-gray-200 cursor-pointer hover:border-gray-400 transition"
        >
          <div className="flex justify-between items-center">
            <div>
              <p className="text-[11px] font-bold text-gray-500 uppercase">Deleted (Trash)</p>
              <h3 className="text-2xl font-black text-gray-700 mt-0.5">{stats.deleted_members}</h3>
            </div>
            <div className="bg-gray-100 text-gray-700 p-3 rounded-xl border border-gray-300">
              <FaUserGraduate size={20} />
            </div>
          </div>
        </div>
      </div>

      {/* Row 3: Dynamic Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Area Chart: New Members & Renewals by Month */}
        <div className="lg:col-span-2 bg-white p-6 rounded-xl shadow-sm border border-gray-200">
          <div className="flex justify-between items-center mb-5 border-b pb-3">
            <div>
              <h3 className="text-base font-bold text-gray-800">New Member Joinings & Tenure Renewals Graph</h3>
              <p className="text-xs text-gray-500">Monthly breakdown for Society Year: {societyYear}</p>
            </div>
          </div>
          
          <div className="h-72 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={monthlyChartData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="colorNew" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                  </linearGradient>
                  <linearGradient id="colorRen" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#f97316" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#f97316" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e5e7eb" />
                <XAxis dataKey="name" tick={{fill: '#6b7280', fontSize: 11}} tickLine={false} axisLine={{stroke: '#e5e7eb'}} />
                <YAxis tick={{fill: '#6b7280', fontSize: 11}} tickLine={false} axisLine={false} />
                <Tooltip />
                <Legend iconType="rect" wrapperStyle={{paddingTop: '15px'}} />
                <Area type="monotone" name="New Members Joined" dataKey="newMembers" stroke="#3b82f6" strokeWidth={3} fillOpacity={1} fill="url(#colorNew)" dot={{r: 4, strokeWidth: 2, fill: '#fff', stroke: '#3b82f6'}} activeDot={{r: 6}} />
                <Area type="monotone" name="Tenure Renewals" dataKey="renewals" stroke="#f97316" strokeWidth={3} fillOpacity={1} fill="url(#colorRen)" dot={{r: 4, strokeWidth: 2, fill: '#fff', stroke: '#f97316'}} activeDot={{r: 6}} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Pie Chart: Dynamic Member Distribution */}
        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200 flex flex-col justify-between">
          <div className="flex justify-between items-start border-b pb-3 mb-2">
            <div>
              <h3 className="text-base font-bold text-gray-800">Member Distribution</h3>
              <p className="text-xs text-gray-500">Live roster segment status</p>
            </div>
            <div className="text-right">
              <span className="text-[10px] text-gray-500 uppercase tracking-wider block font-bold">Total Roster</span>
              <span className="text-2xl font-black text-[#0A2451]">{stats.total_members}</span>
            </div>
          </div>
          
          <div className="h-[250px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={pieData}
                  cx="50%"
                  cy="50%"
                  innerRadius={55}
                  outerRadius={90}
                  paddingAngle={3}
                  dataKey="value"
                  stroke="#fff"
                  strokeWidth={2}
                >
                  {pieData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend 
                  layout="horizontal" 
                  verticalAlign="bottom" 
                  align="center"
                  iconType="circle"
                  formatter={(value, entry: any) => (
                    <span className="text-xs text-gray-700 font-medium">{value}: <b className="text-gray-900">{entry.payload.value}</b></span>
                  )}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Row 4: 🎂 Upcoming Member Birthdays */}
      <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-200 space-y-4">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 border-b pb-3">
          <div>
            <h3 className="text-base font-extrabold text-[#0A2451] flex items-center gap-2">
              <FaBirthdayCake className="text-amber-500" /> Upcoming Member Birthdays
            </h3>
            <p className="text-xs text-gray-500">Celebrate active chapter members' birthdays and send instant greetings</p>
          </div>
          <span className="bg-amber-100 text-amber-800 font-extrabold text-xs px-3 py-1 rounded-full border border-amber-200">
            {upcomingBirthdays.length} Member Birthdays Scheduled
          </span>
        </div>

        {upcomingBirthdays.length === 0 ? (
          <p className="text-xs text-gray-400 italic py-4 text-center">No upcoming birthdays found in the system.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {upcomingBirthdays.slice(0, 8).map((member: any) => {
              const isToday = member.daysUntil === 0;
              const isTomorrow = member.daysUntil === 1;

              return (
                <div
                  key={member.id}
                  className={`p-4 rounded-xl border transition flex flex-col justify-between space-y-3 ${
                    isToday
                      ? 'bg-gradient-to-br from-amber-50 to-pink-50 border-amber-400 shadow-md ring-2 ring-amber-300'
                      : isTomorrow
                      ? 'bg-amber-50/50 border-amber-200'
                      : 'bg-slate-50/70 border-slate-200 hover:border-amber-300'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <img
                      src={
                        member.profile
                          ? member.profile.startsWith('http')
                            ? member.profile
                            : `https://rmbbackend.rmbjaipur.com/${member.profile.replace(/\\/g, '/').replace(/^\/+/, '')}`
                          : 'https://via.placeholder.com/150'
                      }
                      alt={member.name}
                      className="w-12 h-12 rounded-full object-cover border-2 border-amber-400 shadow-sm"
                    />
                    <div className="flex-1 min-w-0">
                      <h4 className="text-sm font-black text-slate-800 truncate">{member.name}</h4>
                      <p className="text-xs text-slate-500 truncate">{member.designation || member.company_name || 'RMB Member'}</p>
                      <div className="flex items-center gap-1.5 mt-1">
                        <span className="text-[10px] font-bold bg-white text-slate-700 px-2 py-0.5 rounded border border-slate-200">
                          📅 {member.formattedDate}
                        </span>
                        <span
                          className={`text-[10px] font-black px-2 py-0.5 rounded uppercase ${
                            isToday
                              ? 'bg-amber-500 text-slate-950 animate-pulse'
                              : isTomorrow
                              ? 'bg-amber-200 text-amber-900'
                              : 'bg-slate-200 text-slate-700'
                          }`}
                        >
                          {member.statusText}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 pt-2 border-t border-slate-200/60">
                    <button
                      onClick={() => {
                        setSelectedBirthdayMember(member);
                        setBirthdayModalMode('WISH');
                        setWaSendResult(null);
                        setFlyerTitle('Happy Birthday');
                        setFlyerMessage("RMB Jaipur Family wishes a Happy Birthday to our\nincredible Member!\nMay this year bring you success, happiness, and\nfulfillment");
                      }}
                      className={`flex-1 py-1.5 px-2 font-bold text-[11px] rounded-lg shadow-sm transition flex items-center justify-center gap-1 cursor-pointer active:scale-95 ${
                        isToday
                          ? 'bg-gradient-to-r from-emerald-600 via-teal-600 to-emerald-600 text-white animate-pulse shadow-emerald-500/20'
                          : 'bg-emerald-600 hover:bg-emerald-700 text-white'
                      }`}
                      title="Send WhatsApp Birthday Template with Poster"
                    >
                      <FaWhatsapp size={12} /> {isToday ? '🎂 Wish Today!' : 'Wish'}
                    </button>

                    <button
                      onClick={() => {
                        setSelectedBirthdayMember(member);
                        setBirthdayModalMode('FLYER');
                        setWaSendResult(null);
                        setFlyerTitle('Happy Birthday');
                        setFlyerMessage("RMB Jaipur Family wishes a Happy Birthday to our\nincredible Member!\nMay this year bring you success, happiness, and\nfulfillment");
                      }}
                      className="py-1.5 px-2.5 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-slate-950 font-black text-[11px] rounded-lg shadow-sm transition flex items-center gap-1 cursor-pointer active:scale-95"
                      title="Generate Birthday Flyer & Send Template"
                    >
                      <FaMagic size={11} /> Flyer
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ================= BIRTHDAY FLYER & WISH STUDIO MODAL ================= */}
      {selectedBirthdayMember && birthdayModalMode && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-6 bg-black/70 backdrop-blur-sm animate-fadeIn overflow-y-auto">
          <div className="relative w-full max-w-2xl bg-white dark:bg-[#0F1C36] rounded-3xl shadow-2xl border border-slate-200 dark:border-white/10 overflow-hidden flex flex-col max-h-[92vh]">
            
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-[#0A2451] via-[#103366] to-[#0A2451] p-4 sm:p-5 text-white flex items-center justify-between border-b border-white/10 flex-shrink-0">
              <div className="flex items-center gap-2.5">
                <div className="p-2 rounded-xl bg-[#FDBC12] text-[#0A2451]">
                  <FaBirthdayCake className="text-lg" />
                </div>
                <div>
                  <h3 className="text-base sm:text-lg font-black text-white">
                    {birthdayModalMode === 'FLYER' ? '🎂 Birthday Flyer Studio' : '🎉 Send Birthday Wishes'}
                  </h3>
                  <p className="text-xs text-amber-200">
                    Rotarian {selectedBirthdayMember.name} • {selectedBirthdayMember.formattedDate}
                  </p>
                </div>
              </div>
              <button
                onClick={() => {
                  setSelectedBirthdayMember(null);
                  setBirthdayModalMode(null);
                  setWaSendResult(null);
                }}
                className="p-2 rounded-xl bg-white/10 hover:bg-white/20 text-white text-sm transition cursor-pointer"
              >
                <FaTimes />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-4 sm:p-6 overflow-y-auto flex-1 space-y-4">
              
              {/* STATUS / SUCCESS NOTIFICATION */}
              {waSendResult && (
                <div className={`p-3.5 rounded-2xl text-xs font-bold flex items-center gap-2 ${
                  waSendResult.success 
                    ? 'bg-emerald-100 dark:bg-emerald-950/60 text-emerald-800 dark:text-emerald-300 border border-emerald-300' 
                    : 'bg-rose-100 dark:bg-rose-950/60 text-rose-800 dark:text-rose-300 border border-rose-300'
                }`}>
                  {waSendResult.success ? <FaCheck className="text-base flex-shrink-0" /> : <FaTimes className="text-base flex-shrink-0" />}
                  <span>{waSendResult.message}</span>
                </div>
              )}

              {/* LIVE LUXURY ROTARY BIRTHDAY CARD (POSTER / FLYER CANVAS) */}
              <div className="flex flex-col items-center justify-center w-full">
                <div className="w-full max-w-[420px] rounded-2xl overflow-hidden shadow-2xl border-4 border-[#FDBC12] bg-[#243758] relative">
                  <BirthdayCard
                    ref={flyerPosterRef}
                    image={
                      selectedBirthdayMember.profile
                        ? (selectedBirthdayMember.profile.startsWith('http')
                            ? selectedBirthdayMember.profile
                            : `https://rmbbackend.rmbjaipur.com/${selectedBirthdayMember.profile.replace(/\\/g, '/').replace(/^\/+/, '')}`)
                        : undefined
                    }
                    name={selectedBirthdayMember.name}
                    date={formatBirthdayDateForPoster(selectedBirthdayMember.dob, selectedBirthdayMember.formattedDate)}
                    title={flyerTitle}
                    message={flyerMessage}
                  />
                </div>
              </div>

              {/* Flyer Text Customizer */}
              <div className="p-3.5 bg-slate-50 dark:bg-white/5 rounded-2xl border border-slate-200 dark:border-white/10 space-y-2.5">
                <div className="flex items-center justify-between text-xs font-bold text-slate-700 dark:text-slate-200">
                  <span className="flex items-center gap-1.5">
                    <FaMagic className="text-amber-500" />
                    <span>Customize Poster Flyer Text (Updates Live)</span>
                  </span>
                  <button
                    type="button"
                    onClick={() => {
                      setFlyerTitle('Happy Birthday');
                      setFlyerMessage(
                        "RMB Jaipur Family wishes a Happy Birthday to our\nincredible Member!\nMay this year bring you success, happiness, and\nfulfillment"
                      );
                    }}
                    className="text-[10px] text-amber-600 dark:text-amber-400 hover:underline cursor-pointer font-bold"
                  >
                    Reset Defaults
                  </button>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5 text-xs">
                  <div className="sm:col-span-1">
                    <label className="block text-[11px] font-semibold text-slate-600 dark:text-slate-300 mb-1">
                      Poster Title:
                    </label>
                    <input
                      type="text"
                      value={flyerTitle}
                      onChange={(e) => setFlyerTitle(e.target.value)}
                      placeholder="Happy Birthday"
                      className="w-full px-3 py-1.5 rounded-lg border border-slate-300 dark:border-white/10 bg-white dark:bg-[#0A162D] text-slate-800 dark:text-white text-xs font-semibold focus:outline-none focus:ring-2 focus:ring-amber-400"
                    />
                  </div>

                  <div className="sm:col-span-2">
                    <label className="block text-[11px] font-semibold text-slate-600 dark:text-slate-300 mb-1">
                      Wish Message (Line by Line):
                    </label>
                    <textarea
                      rows={3}
                      value={flyerMessage}
                      onChange={(e) => setFlyerMessage(e.target.value)}
                      placeholder="Type wish message here..."
                      className="w-full px-3 py-1.5 rounded-lg border border-slate-300 dark:border-white/10 bg-white dark:bg-[#0A162D] text-slate-800 dark:text-white text-xs font-medium focus:outline-none focus:ring-2 focus:ring-amber-400 resize-none"
                    />
                  </div>
                </div>
              </div>

              {/* Delivery Details Callout */}
              <div className="p-3 bg-slate-50 dark:bg-white/5 rounded-2xl border border-slate-200 dark:border-white/10 text-xs space-y-1">
                <div className="flex items-center justify-between font-bold text-slate-700 dark:text-slate-200">
                  <span>📱 Member Phone:</span>
                  <span className="text-[#0A2451] dark:text-[#FDBC12] font-black">
                    {selectedBirthdayMember.number ? selectedBirthdayMember.number : 'No number in profile (Will send to Admin)'}
                  </span>
                </div>
                <div className="flex items-center justify-between font-bold text-slate-700 dark:text-slate-200">
                  <span>🛡️ CC Admin Recipient:</span>
                  <span className="text-emerald-600 dark:text-emerald-400 font-black">
                    917301020313
                  </span>
                </div>
                <div className="flex items-center justify-between text-[11px] text-slate-500 dark:text-slate-400 pt-1 border-t border-slate-200/60 dark:border-white/10">
                  <span>Template &amp; Header Media:</span>
                  <span className="font-semibold text-slate-600 dark:text-slate-300">birthday_wishes (IMAGE: Luxury RMB Birthday Flyer)</span>
                </div>
              </div>
            </div>

            {/* Modal Footer Controls */}
            <div className="bg-slate-50 dark:bg-[#0A162D] px-5 py-4 border-t border-slate-200 dark:border-white/10 flex flex-wrap items-center justify-between gap-2.5 flex-shrink-0">
              <button
                type="button"
                onClick={() => {
                  setSelectedBirthdayMember(null);
                  setBirthdayModalMode(null);
                  setWaSendResult(null);
                }}
                className="px-4 py-2 rounded-xl text-xs font-bold text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-white/10 transition"
              >
                Cancel
              </button>

              <div className="flex flex-wrap items-center gap-2">
                {/* Download Flyer Button */}
                <button
                  type="button"
                  onClick={() => handleDownloadFlyer(selectedBirthdayMember.name)}
                  className="px-3.5 py-2 rounded-xl bg-slate-200 hover:bg-slate-300 dark:bg-white/10 dark:hover:bg-white/20 text-slate-800 dark:text-white font-bold text-xs flex items-center gap-1.5 transition cursor-pointer"
                >
                  <FaDownload />
                  <span>Download Flyer</span>
                </button>

                {/* Direct WhatsApp Web Chat Button */}
                <button
                  type="button"
                  onClick={() => {
                    const cleanPhone = (selectedBirthdayMember.number || '').replace(/\D/g, '');
                    const phoneWith91 = cleanPhone.length === 10 ? `91${cleanPhone}` : cleanPhone;
                    const msg = encodeURIComponent(
                      `🎂 *Happy Birthday Rotarian ${selectedBirthdayMember.name}!* 🎉\n\nOn behalf of the entire RMB Jaipur Chapter, we wish you joy, success, and continuous prosperity!\n\n_Rotary Means Business Fellowship_`
                    );
                    const url = phoneWith91
                      ? `https://api.whatsapp.com/send?phone=${phoneWith91}&text=${msg}`
                      : `https://api.whatsapp.com/send?text=${msg}`;
                    window.open(url, '_blank');
                  }}
                  className="px-3 py-2 rounded-xl bg-emerald-100 hover:bg-emerald-200 dark:bg-emerald-950/50 dark:hover:bg-emerald-900/50 text-emerald-800 dark:text-emerald-300 font-bold text-xs flex items-center gap-1.5 transition cursor-pointer"
                >
                  <FaWhatsapp />
                  <span>Direct Chat</span>
                </button>

                {/* Send Official GlobeBot WhatsApp Campaign */}
                <button
                  type="button"
                  onClick={() => handleSendBirthdayTemplate(selectedBirthdayMember)}
                  disabled={isSendingWa}
                  className="px-5 py-2 rounded-xl font-black text-xs bg-[#FDBC12] hover:bg-amber-400 text-[#0A2451] shadow-md transition flex items-center gap-1.5 cursor-pointer disabled:opacity-50 active:scale-95"
                >
                  {isSendingWa ? <FaSyncAlt className="animate-spin" /> : <FaPaperPlane />}
                  <span>{isSendingWa ? 'Sending Template...' : 'Send WhatsApp Template (GlobeBot)'}</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DashbordOverview;
